import { useEffect, useState, useCallback, useRef } from "react";

/**
 * Admin infinite-scroll list hook.
 *
 * Shared by AdminUsersTab / AdminDonorsTab / AdminPromotersTab — these
 * three lists all behave the same way:
 *
 *   • Mount fetches page 0 (offset=0, limit=PAGE_SIZE)
 *   • Sentinel element near the list footer triggers loadMore
 *   • When `params` (filters / search / dummy toggle) change, we
 *     reset to offset=0 and refetch.
 *
 * The hook is deliberately dumb about the API shape: it expects the
 * fetcher to return `{ total, items }`, which all three Sprint B
 * endpoints do. We don't try to dedupe items — if the backend returns
 * duplicates across pages, that's a backend bug we'd rather surface
 * than paper over.
 *
 * Usage:
 *
 *   const { items, total, loading, hasMore, sentinelRef, reset } =
 *     useAdminInfiniteList({
 *       fetchPage: api.adminUsersV2,
 *       params: { q, filter, include_dummy: includeDummy },
 *     });
 *
 *   {items.map(...)}
 *   <div ref={sentinelRef} />     // hits => loadMore
 */
const PAGE_SIZE = 20;

export default function useAdminInfiniteList({ fetchPage, params }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exhausted, setExhausted] = useState(false);
  const offsetRef = useRef(0);
  const inflightRef = useRef(false);
  // Stringify params for the dependency comparator so React's
  // identity-based diff doesn't refetch on every parent render.
  const paramsKey = JSON.stringify(params || {});

  const loadMore = useCallback(async () => {
    if (inflightRef.current || exhausted) return;
    inflightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPage({
        ...params,
        limit: PAGE_SIZE,
        offset: offsetRef.current,
      });
      const newItems = res?.items || [];
      setItems((prev) => [...prev, ...newItems]);
      setTotal(res?.total ?? 0);
      offsetRef.current += newItems.length;
      if (newItems.length < PAGE_SIZE) setExhausted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "טעינה נכשלה");
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, paramsKey, exhausted]);

  const reset = useCallback(() => {
    offsetRef.current = 0;
    inflightRef.current = false;
    setItems([]);
    setTotal(0);
    setExhausted(false);
    setError(null);
  }, []);

  // Reset & initial fetch when params change.
  useEffect(() => {
    reset();
    // schedule the initial fetch on the next tick so the reset has
    // landed before we call loadMore (which short-circuits on
    // inflightRef).
    const t = setTimeout(() => {
      offsetRef.current = 0;
      inflightRef.current = false;
      // Inline the fetch to avoid stale-closure traps with paramsKey
      (async () => {
        inflightRef.current = true;
        setLoading(true);
        try {
          const res = await fetchPage({
            ...params,
            limit: PAGE_SIZE,
            offset: 0,
          });
          const newItems = res?.items || [];
          setItems(newItems);
          setTotal(res?.total ?? 0);
          offsetRef.current = newItems.length;
          if (newItems.length < PAGE_SIZE) setExhausted(true);
          else setExhausted(false);
        } catch (e) {
          setError(e?.response?.data?.detail || "טעינה נכשלה");
        } finally {
          setLoading(false);
          inflightRef.current = false;
        }
      })();
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Sentinel + IntersectionObserver
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return {
    items,
    total,
    loading,
    hasMore: !exhausted,
    error,
    sentinelRef,
    reset,
    loadMore,
  };
}
